import { useCallback, useMemo } from "react";
import { useObservable, useSelector } from "@legendapp/state/react";

type SetStateAction<T> = T | ((prevState: T) => T);

export function useLegendState<T>(initialState: T | (() => T)) {
  const resolvedInitialState = useMemo(
    () =>
      typeof initialState === "function"
        ? (initialState as () => T)()
        : initialState,
    [initialState]
  );
  const state$ = useObservable<T>(resolvedInitialState);
  const state = useSelector(state$);

  const setState = useCallback((nextState: SetStateAction<T>) => {
    if (typeof nextState === "function") {
      const updater = nextState as (prevState: T) => T;
      state$.set((prev) => updater(prev));
      return;
    }
    state$.set(nextState);
  }, [state$]);

  return [state, setState] as const;
}
